// src/pages/admin/payment-methods/PaymentMethods.jsx
import React, { useState, useEffect, useMemo } from "react";
import {
  Box, Button, Typography, Dialog, DialogActions, DialogContent, DialogTitle,
  useMediaQuery, useTheme, Alert, Snackbar, Stack,
} from "@mui/material";
import { usePaymentMethods } from "../../../hooks/usePaymentMethods";
import PaymentMethodTable from "../../../components/PaymentMethodTable";
import AddPaymentMethodForm from "../../../components/AddPaymentMethodForm";
import { usePageContext } from "../../../context/PageContext";
import { useTranslation } from "react-i18next";

const PaymentMethods = () => {
  const { t } = useTranslation("settings");
  const { currentPage, setCurrentPage } = usePageContext();
  const [pageSize, setPageSize] = useState(50);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const {
    data,
    error,
    refetch,
    updateMutation,
    updateStatusMutation, // ✅ vem do hook (PUT parcial)
    deleteMutation,
    isLoading,
    isFetching,
    isRefetching,
  } = usePaymentMethods(currentPage, pageSize, search);

  useEffect(() => {
    if (error) setErrorMessage("Erro ao carregar formas de pagamento. Por favor, tente novamente.");
    else setErrorMessage(null);
  }, [error]);

  useEffect(() => {
    const t = setTimeout(() => setSearch(String(searchInput ?? "").trim()), 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const handleEditPaymentMethod = (row) => {
    setSelectedPaymentMethod({ id: row.id }); // form busca o detalhe
    setErrorMessage(null);
  };

  const handleDeletePaymentMethod = (id) => {
    setConfirmDelete(id);
    setErrorMessage(null);
  };

  const confirmDeletePaymentMethod = () => {
    if (!confirmDelete) return;
    setIsProcessing(true);
    deleteMutation.mutate(confirmDelete, {
      onSuccess: () => {
        setSnack({ open: true, message: "Forma de pagamento excluída com sucesso.", severity: "success" });
        setConfirmDelete(null);
        refetch();
      },
      onError: (err) => {
        setErrorMessage(err?.response?.data?.message || "Erro ao excluir a forma de pagamento. Tente novamente.");
        setSnack({ open: true, message: "Falha ao excluir.", severity: "error" });
      },
      onSettled: () => setIsProcessing(false),
    });
  };

  const handleToggleStatus = (row) => {
    const newStatus = Number(row.status) === 1 ? 0 : 1;
    updateStatusMutation.mutate(
      { id: row.id, status: newStatus },
      {
        onSuccess: () =>
          setSnack({
            open: true,
            message: `Forma ${newStatus === 1 ? "ativada" : "inativada"} com sucesso.`,
            severity: "success",
          }),
        onError: () => setSnack({ open: true, message: "Falha ao atualizar status.", severity: "error" }),
      }
    );
  };

  const handleDeleteSelected = async (ids) => {
    if (!ids?.length) return;
    setIsProcessing(true);
    setErrorMessage(null);
    const results = await Promise.allSettled(
      ids.map((id) => deleteMutation.mutateAsync(id))
    );
    const failed = results.filter((r) => r.status === "rejected");
    if (failed.length > 0) {
      setErrorMessage(
        failed.length === ids.length
          ? "Erro ao excluir as formas de pagamento."
          : `${failed.length} de ${ids.length} exclusão(ões) falharam.`
      );
      setSnack({ open: true, message: "Falha em algumas exclusões.", severity: "error" });
    } else {
      setSnack({ open: true, message: "Formas de pagamento excluídas com sucesso.", severity: "success" });
    }
    refetch();
    setIsProcessing(false);
  };

  const handlePageChange = (newPage) => setCurrentPage(newPage + 1);
  const handlePageSizeChange = (newPageSize) => setPageSize(newPageSize);
  const handleCloseForm = () => {
    setSelectedPaymentMethod(null);
    setErrorMessage(null);
  };

  const busy = isLoading || isFetching || isRefetching || isProcessing;
  const pagination = data?.pagination || { total: 0, page: 1, totalPages: 1 };

  const headerRight = useMemo(
    () => (
      <Stack direction={isMobile ? "column" : "row"} spacing={1} alignItems="center">
        <Button variant="contained" color="primary" fullWidth={isMobile} onClick={() => setSelectedPaymentMethod({})}>
          {t("paymentMethodsPage.add")}
        </Button>
      </Stack>
    ),
    [isMobile]
  );

  return (
    <Box sx={{ pt: 1 }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          justifyContent: "space-between",
          alignItems: isMobile ? "flex-start" : "center",
          mb: 2,
          gap: isMobile ? 2 : 0,
        }}
      >
        <Typography variant="h4" gutterBottom>{t("paymentMethodsPage.title")}</Typography>
        {headerRight}
      </Box>

      {errorMessage && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="error">{errorMessage}</Alert>
        </Box>
      )}

      {selectedPaymentMethod ? (
        <AddPaymentMethodForm
          paymentMethod={selectedPaymentMethod}
          isLoading={busy}
          onClose={handleCloseForm}
          onSubmit={(payload) => {
            // Retorna Promise para o form saber quando salvou (e.g. resetar "dirty")
            return new Promise((resolve, reject) => {
              setIsProcessing(true);
              updateMutation.mutate(payload, {
                onSuccess: (resp) => {
                  setSnack({
                    open: true,
                    message: "Forma de pagamento salva com sucesso.",
                    severity: "success",
                  });
                  refetch();

                  // Mantém no form (não fecha) e garante que "novo" vire "edição"
                  const savedId =
                    resp?.data?.id ??
                    resp?.data?.data?.id ??
                    payload?.id ??
                    null;
                  if (savedId) setSelectedPaymentMethod({ id: savedId });

                  resolve(resp);
                },
                onError: (err) => {
                  setErrorMessage(err?.response?.data?.message || "Erro ao salvar a forma de pagamento.");
                  setSnack({ open: true, message: "Falha ao salvar.", severity: "error" });
                  reject(err);
                },
                onSettled: () => setIsProcessing(false),
              });
            });
          }}
        />
      ) : (
        <PaymentMethodTable
          paymentMethods={data?.paymentMethods || []}
          rowCount={pagination.total || 0}
          pageSize={pageSize}
          page={currentPage - 1}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          onSearch={(term) => setSearchInput(term == null ? "" : String(term))}
          isLoading={busy}
          onEditMethod={handleEditPaymentMethod}
          onDeleteMethod={handleDeletePaymentMethod}
          onDeleteSelected={handleDeleteSelected}
          onToggleStatus={handleToggleStatus}
        />
      )}

      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)}>
        <DialogTitle>{t("shared.deleteConfirmation")}</DialogTitle>
        <DialogContent>
          <Typography>
            {isProcessing ? t("shared.processingDelete") : t("paymentMethodsPage.deleteConfirm")}
          </Typography>
        </DialogContent>
        <DialogActions>
          {!isProcessing && (
            <>
              <Button onClick={() => setConfirmDelete(null)}>{t("shared.cancel")}</Button>
              <Button color="error" onClick={confirmDeletePaymentMethod}>{t("shared.delete")}</Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack({ ...snack, open: false })}
        message={snack.message}
      />
    </Box>
  );
};

export default PaymentMethods;
